/** */
import { AutomationProvider } from 'contexts/automation';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';

import { useFeatureFlag } from 'hooks/go-flags';
import { addOneFlagToStore } from 'store/actions/flag';

import SideNav from './side-nav/side-nav';
import ErrorBoundary from 'app/shared/wrappers/error-boundary/error-boundary';
import { EditorLayout } from './editors';
import { ModuleLayout } from './modules';
import { addMetadata } from 'store/actions/metadata';
import './member.css';
import { PreOrganizationModule } from './organization';
import { PermissionsContext, PermissionsProvider } from 'contexts/permissions';
import Spinner from 'app/shared/spinners/spinner-50/spinner-50';
import { AffiliateArea } from './affiliate';
import { AuthRefresher } from './auth';
import {
  SubscriptionPurchaseContext,
  SubscriptionPurchaseProvider
} from 'contexts/checkout/subscription';
import { SubscriptionPurchaseModal } from './modules/checkout/modal/purchase';
import { VirtualAccountContext, VirtualAccountProvider } from 'contexts/checkout/virtual';
import { TenantProvider } from 'contexts/tenant';
import { VirtualAccountCheckout } from './modules/checkout/modal/virtual';
import GmModal from 'app/shared/modal/modal';
import { OrgnisationDeactivationNotice } from './organization/notice';
import { OnboardingTour } from 'app/shared/onboarding/tour';
import { OnboardingCallModal } from 'app/shared/onboarding/call-modal';
import { OnboardingCallModalProvider } from 'contexts/onboarding/call-modal';
import { UpsellProvider } from 'contexts/upsell';
import { useTenantService } from 'hooks/iam/tenant';

const MemberArea = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { isFeatureAllowed } = useFeatureFlag();
  const {
    id: user_id,
    account_type,
    tenant_id,
    memberships,
    onboarding_completed
  } = useSelector((state) => state.user_data);
  const is_first_org = (memberships || []).length <= 1;
  const { fetchTenant } = useTenantService();

  const [is_preparing, setIsPreparing] = useState(true);
  const [open_menu_tray, setOpenMenuTray] = useState(false);

  useEffect(() => {
    const setup = async () => {
      const acceptance_token = window.localStorage.getItem('acceptance');
      const has_membership = (memberships || []).some((doc) => doc.status === 'active');
      const has_active_membership = (memberships || []).some((doc) => doc.is_active);
      if (account_type === 'affiliate') {
        history('/affiliates');
      } else if (!has_membership && !acceptance_token) {
        history('/organizations');
      } else if (has_active_membership && onboarding_completed === false && is_first_org) {
        const { tenant } = await fetchTenant(tenant_id);
        if (tenant?.creator_id === user_id) {
          history('/organizations/qualification');
        }
      }

      setIsPreparing(() => false);
    };
    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMobileFlag();
    window.addEventListener('resize', () => {
      setMobileFlag();
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isFeatureAllowed('Feature.EmailAutomation', { tenant_id }).then(({ is_permitted }) => {
      dispatch(addOneFlagToStore({ id: 'email_automation', is_permitted }));
    });

    isFeatureAllowed('Feature.ContactUploads', { tenant_id }).then(({ is_permitted }) => {
      dispatch(addOneFlagToStore({ id: 'contact_upload', is_permitted }));
    });

    isFeatureAllowed('Feature.EmailFootnote', { tenant_id }).then(({ is_permitted }) => {
      dispatch(addOneFlagToStore({ id: 'email_footnote', is_permitted }));
    });

    isFeatureAllowed('Feature.Billing', { tenant_id }).then(({ is_permitted }) => {
      dispatch(addOneFlagToStore({ id: 'billing', is_permitted }));
    });

    isFeatureAllowed('Feature.UserManagement', { tenant_id }).then(({ is_permitted }) => {
      dispatch(addOneFlagToStore({ id: 'user_management', is_permitted }));
    });
  }, [dispatch, isFeatureAllowed, tenant_id]);

  const setMobileFlag = () => {
    if (window.screen.width < 768) {
      dispatch(addMetadata({ is_mobile_view: true }));
    } else dispatch(addMetadata({ is_mobile_view: false }));
  };

  return (
    <PermissionsProvider>
      <TenantProvider>
        <VirtualAccountProvider>
          <SubscriptionPurchaseProvider>
            <OnboardingCallModalProvider>
              <UpsellProvider>
                <AutomationProvider>
                  <AuthRefresher />
                  <div className="member-area-wrapper">
                    <section className="app-body">
                      <Routes>
                        <Route path="affiliates/*" element={<AffiliateArea />} />
                        <Route path="organizations/*" element={<PreOrganizationModule />} />
                        <Route
                          path="/*"
                          element={
                            is_preparing ? (
                              <div
                                style={{
                                  width: '100%',
                                  height: '100vh',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center'
                                }}
                              >
                                <Spinner />
                              </div>
                            ) : (
                              <MemberLayout
                                open_menu_tray={open_menu_tray}
                                setOpenMenuTray={setOpenMenuTray}
                              />
                            )
                          }
                        />
                      </Routes>
                    </section>
                  </div>
                </AutomationProvider>
              </UpsellProvider>
            </OnboardingCallModalProvider>
          </SubscriptionPurchaseProvider>
        </VirtualAccountProvider>
      </TenantProvider>
    </PermissionsProvider>
  );
};

const MemberLayout = ({ open_menu_tray, setOpenMenuTray }) => {
  const { email_automation, user_management } = useSelector((state) => state.flags);
  const { memberships } = useSelector((state) => state.user_data);
  const { is_loading: is_loading_permissions } = useContext(PermissionsContext);
  const { show_modal } = useContext(SubscriptionPurchaseContext);
  const { closeModal: closeVirtualAccountModal, show_modal: show_virtual_account_modal } =
    useContext(VirtualAccountContext);

  const spinnerLayoutStyles = {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const has_active_membership = memberships.find((membership) => membership.is_active);
  return memberships && memberships.length > 0 && !has_active_membership ? (
    <OrgnisationDeactivationNotice />
  ) : (
    <>
      {!email_automation || !user_management || is_loading_permissions ? (
        <div style={spinnerLayoutStyles}>
          <Spinner />
        </div>
      ) : (
        <>
          <SideNav open_tray={open_menu_tray} onTrayClose={() => setOpenMenuTray(false)} />
          <ErrorBoundary className="app-content">
            <Routes>
              <Route path="/editor/*" element={<EditorLayout />} />
              <Route path="/*" element={<ModuleLayout setOpenMenuTray={setOpenMenuTray} />} />
            </Routes>
          </ErrorBoundary>
          <OnboardingTour />
        </>
      )}
      {show_modal && <SubscriptionPurchaseModal />}
      <GmModal
        title="Complete Your Payment via Bank Transfer"
        show_title
        show_modal={show_virtual_account_modal}
        onClose={closeVirtualAccountModal}
      >
        <VirtualAccountCheckout />
      </GmModal>
      <OnboardingCallModal />
    </>
  );
};

export default MemberArea;
